import { Controller } from "@hotwired/stimulus"

// Copie du corps de l'email dans le presse-papiers — remplace clipboard.js
// 1.5.8 (CDN), retirée en vague 6.
//
// À quoi ça sert : sur les mails de demande, le bouton « Générer » enregistre
// l'événement puis laisse l'utilisateur coller le corps du mail dans son propre
// client. La copie doit être RICHE, sinon toute la mise en forme est perdue.
//
// Le `mailto:` qui accompagnait cette copie a été retiré le 26.08.2026 : il
// ouvrait Outlook tout seul, ce dont personne ne voulait. Le presse-papiers est
// désormais la seule voie de sortie du texte — d'où l'importance de la copie
// riche, qu'aucun `mailto:` n'aurait de toute façon su transporter.
//
// PARITÉ : clipboard.js visait `.note-editable`, le DOM interne de summernote.
// Comme summernote ne se montait jamais dans ces modales (cf.
// app/helpers/rich_text_helper.rb), la cible n'existait pas et le bouton ne
// copiait RIEN. Le contrôleur vise maintenant l'éditeur Trix par son id.
export default class extends Controller {
  // id du <trix-editor> produit par le helper `rich_text_editor_tag`
  static values = { source: String }

  connect() {
    this.resetTimer = null
  }

  disconnect() {
    if (this.resetTimer) window.clearTimeout(this.resetTimer)
  }

  // data-action="click->clipboard#copy"
  // On NE bloque PAS l'événement : le bouton reste un submit, la requête part
  // en parallèle. `navigator.clipboard.write` est asynchrone mais démarre dans
  // le geste utilisateur, ce qui suffit à l'autoriser ; legacy.js ne recharge
  // la page qu'une seconde plus tard.
  copy() {
    const editor = document.getElementById(this.sourceValue)
    if (!editor) return

    // <trix-editor>.value renvoie la valeur du hidden associé, c'est-à-dire
    // exactement le HTML que le formulaire soumettrait.
    const html = (editor.value || "").toString()
    const text = editor.editor ? editor.editor.getDocument().toString() : editor.textContent

    this.write(html, text).then(
      (types) => {
        this.feedback("clipboard-copied")
        // `clipboard:copied` — les formats réellement déposés. Seul moyen de
        // vérifier la copie riche en test : Chrome headless refuse la
        // permission clipboard-read, quoi qu'on lui accorde par CDP.
        this.dispatch("copied", { detail: { types } })
      },
      (error) => {
        this.feedback("clipboard-failed")
        this.dispatch("failed", { detail: { error } })
      }
    )
  }

  write(html, text) {
    if (!navigator.clipboard) {
      return Promise.reject(new Error("navigator.clipboard indisponible"))
    }

    const plain = () => navigator.clipboard.writeText(text).then(() => ["text/plain"])

    // ClipboardItem manque encore à quelques navigateurs : on se rabat sur le
    // texte brut plutôt que de ne rien copier.
    if (!window.ClipboardItem) return plain()

    const item = new window.ClipboardItem({
      "text/html": new Blob([html], { type: "text/html" }),
      "text/plain": new Blob([text], { type: "text/plain" })
    })

    return navigator.clipboard.write([item]).then(() => ["text/html", "text/plain"], plain)
  }

  feedback(className) {
    this.element.classList.remove("clipboard-copied", "clipboard-failed")
    this.element.classList.add(className)

    if (this.resetTimer) window.clearTimeout(this.resetTimer)
    this.resetTimer = window.setTimeout(() => {
      this.element.classList.remove(className)
    }, 2000)
  }
}
